import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIntegrationName } from "./useIntegrationName";
import { CapsuleButton, CustomNumberInput } from "../../../Everou/_components";
import { useDispatch } from "react-redux";
import { MIN_TIME } from "../../../Everou/_constants";
import { useError } from "../../../Everou/_hooks/useError";
import { Icons } from "../../../Everou/_assets";
import moment from "moment";
import { TimeHelper } from "../../../Everou/_helpers";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { uiAlertActions } from "../../../Everou/_actions/uiAlert.actions";
import { CheckInOutFields } from "../../../Everou/IntegrationsModule/components/CheckInOutFields";
import { IntegrationSectionTitle } from "../../../Everou/IntegrationsModule/components/IntegrationSectionTitle";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onSetInitialStatus: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
}

export function PMSAvantioIntegrationInput({
    integration,
    onDelete,
    index,
    onToggleEditable }: IntegrationInputProps) {

    const { t } = useTranslation();
    const { templateName, nameInput } = useIntegrationName(integration);
    const dispatch = useDispatch();

    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [createPublicUrl, setCreatePublicUrl] = useState(integration.createPublicUrl ?? true);
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL ?? true);
    const [daysForInvitationCreate, setDaysForInvitationCreate] = useState(integration.daysForInvitationCreate ?? 1);
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? '16:00');
    const [checkOut, setCheckOut] = useState(integration.check_out ?? '12:00');

    useEffect(() => {
        setApikeyValue(integration.apikey ?? '');
        setCreatePublicUrl(integration.createPublicUrl ?? true);
        setSendInvitationURL(integration.sendInvitationURL ?? true);
        setDaysForInvitationCreate(integration.daysForInvitationCreate ?? 1);
        setCheckIn(integration.check_in ?? '16:00');
        setCheckOut(integration.check_out ?? '12:00');
    }, [integration]);

    const isLegacyIcal = useMemo(() => !!integration.uid && !integration.apikey, [integration.uid, integration.apikey]);

    const canSave = useMemo(() => {
        if (hasError) return false;
        return (
            !!apikeyValue &&
            (
                !integration.uid ||
                apikeyValue !== integration.apikey ||
                createPublicUrl !== integration.createPublicUrl ||
                sendInvitationURL !== integration.sendInvitationURL ||
                daysForInvitationCreate !== integration.daysForInvitationCreate ||
                checkIn !== integration.check_in ||
                checkOut !== integration.check_out
            )
        );
    }, [
        hasError,
        apikeyValue,
        createPublicUrl,
        sendInvitationURL,
        daysForInvitationCreate,
        checkIn,
        checkOut,
        integration.uid,
        integration.apikey,
        integration.createPublicUrl,
        integration.sendInvitationURL,
        integration.daysForInvitationCreate,
        integration.check_in,
        integration.check_out,
    ]);

    const onSave = useCallback(async () => {
        const editedIntegration: Integration = {
            name: templateName,
            apikey: apikeyValue,
            company: integration.company,
            createPublicUrl,
            sendInvitationURL,
            daysForInvitationCreate,
            check_in: checkIn,
            check_out: checkOut,
            uid: integration.uid,
        }
        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
            dispatch(uiAlertActions.success(t("mod_integrations_saved_successfully")));
        } catch (err: any) {
            setError(err.message);
        }
    }, [
        templateName,
        apikeyValue,
        createPublicUrl,
        sendInvitationURL,
        daysForInvitationCreate,
        checkIn,
        checkOut,
        dispatch,
        setError,
        t,
        integration.company,
        integration.uid,
    ]);

    if (isLegacyIcal) {
        return (
            <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
                <div className="col-span-full">
                    <input
                        className="cna col-span-full w-100 p-2 c-h-min c-input-bordered"
                        type="text"
                        readOnly
                        value={integration.link ?? ''}
                        placeholder={t("global_ical_link")}
                        onChange={() => undefined}
                    />
                </div>
                <div className="d-flex justify-content-end">
                    <CapsuleButton
                        onClick={() => onDelete(index)}
                        classNameExtra="mr-2"
                        // eslint-disable-next-line react/style-prop-object
                        style="DANGER"
                        size="SMALL"
                        icon={Icons.trash}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            <IntegrationSectionTitle title={t("mod_integrations_section_credentials")} />
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">API Key</label>
                <input
                    className={`${hasError ? 'input-error' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    value={apikeyValue}
                    placeholder={"API Key"}
                    onChange={(e) => {
                        setApikeyValue(e.target.value?.trim());
                        disableError();
                    }}
                />
            </div>

            <div className="col-span-full mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_delivery")} />
            </div>
            <div className="col-span-full d-flex align-items-start c-bg-grey-250 c-rounded-big p-2">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "18px", height: "18px" }}
                />
                <span className="small text-muted">{t("mod_integrations_avantio_delivery_info")}</span>
            </div>

            <div className="col-span-full mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_schedule")} />
            </div>
            <CheckInOutFields
                checkIn={checkIn}
                checkOut={checkOut}
                onChangeCheckIn={setCheckIn}
                onChangeCheckOut={setCheckOut}
            />

            <div className="col-span-full d-flex align-items-center justify-content-between">
                <ToolTipWrapper tooltip={t("mod_integrations_days_for_invitation_create_tooltip")}>
                    <label className="font-weight-bold mb-0 mr-2">{t("mod_integrations_days_for_invitation_create")}</label>
                </ToolTipWrapper>
                <CustomNumberInput
                    value={daysForInvitationCreate}
                    min={1}
                    max={30}
                    onChange={setDaysForInvitationCreate}
                />
            </div>

            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid && (
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    )}
                </div>
                <CapsuleButton
                    size="SMALL"
                    isEnabled={canSave}
                    onClick={onSave}
                    text={t("global_save")}
                />
            </div>
            {errorMessage && (
                <div className="col-span-full text-danger">{errorMessage}</div>
            )}
        </div>
    );
}
